import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import {
  Bell,
  Package,
  Truck,
  Calendar,
  Info,
  MessageSquare,
  CheckCheck,
  Mail,
  MailOpen,
  Filter,
  Settings,
} from 'lucide-react';
import { apiFetch } from '../../../core/shared/hooks/useApi.js';
import clsx from 'clsx';
import {
  useNotificaties,
  useNotificatieCount,
  useMarkeerGelezen,
  useMarkeerOngelezen,
  useMarkeerAllesGelezen,
  type Notificatie,
} from '../hooks/useNotificaties.js';
import { useSocketEvent } from '../../../core/shared/hooks/useSocket.js';

// ─── Type filter config ────────────────────────────────────────

type FilterType = 'ALLE' | 'DATABUS_EVENT' | 'DOSSIER_UPDATE' | 'AFSPRAAK' | 'SYSTEEM' | 'BERICHT' | 'PLANNING' | 'VOORRAAD';

const FILTERS: { key: FilterType; label: string; icon: React.ReactNode }[] = [
  { key: 'ALLE', label: 'Alle', icon: <Bell size={14} /> },
  { key: 'DOSSIER_UPDATE', label: 'Dossier', icon: <Truck size={14} /> },
  { key: 'BERICHT', label: 'Bericht', icon: <MessageSquare size={14} /> },
  { key: 'PLANNING', label: 'Planning', icon: <Calendar size={14} /> },
  { key: 'DATABUS_EVENT', label: 'Levering', icon: <Package size={14} /> },
  { key: 'VOORRAAD', label: 'Voorraad', icon: <Package size={14} /> },
  { key: 'SYSTEEM', label: 'Systeem', icon: <Info size={14} /> },
];

// ─── Helpers ────────────────────────────────────────────────────

function tijdGeleden(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.max(0, now - then);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'zojuist';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min geleden`;
  const uur = Math.floor(min / 60);
  if (uur < 24) return `${uur} uur geleden`;
  const dag = Math.floor(uur / 24);
  if (dag === 1) return 'gisteren';
  if (dag < 7) return `${dag} dagen geleden`;
  return new Date(dateStr).toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' });
}

function datumGroep(dateStr: string): string {
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const itemDate = new Date(d);
  itemDate.setHours(0, 0, 0, 0);

  const diff = today.getTime() - itemDate.getTime();
  const days = Math.floor(diff / 86400000);

  if (days === 0) return 'Vandaag';
  if (days === 1) return 'Gisteren';
  if (days < 7) return 'Deze week';
  if (days < 30) return 'Deze maand';
  return 'Ouder';
}

function TypeIcoon({ type }: { type: string }) {
  switch (type) {
    case 'DATABUS_EVENT':
      return <Package size={18} className="text-blue-500" />;
    case 'DOSSIER_UPDATE':
      return <Truck size={18} className="text-green-500" />;
    case 'AFSPRAAK':
    case 'PLANNING':
      return <Calendar size={18} className="text-amber-500" />;
    case 'BERICHT':
      return <MessageSquare size={18} className="text-brand" />;
    case 'VOORRAAD':
      return <Package size={18} className="text-purple-500" />;
    default:
      return <Info size={18} className="text-gray-400" />;
  }
}

function typeLabelKort(type: string): string {
  switch (type) {
    case 'DATABUS_EVENT': return 'Levering';
    case 'DOSSIER_UPDATE': return 'Dossier';
    case 'AFSPRAAK': return 'Afspraak';
    case 'PLANNING': return 'Planning';
    case 'BERICHT': return 'Bericht';
    case 'VOORRAAD': return 'Voorraad';
    case 'SYSTEEM': return 'Systeem';
    default: return type;
  }
}

// ─── NotificatieRij ─────────────────────────────────────────────

function NotificatieRij({
  notificatie,
  onNavigate,
  onToggleGelezen,
}: {
  notificatie: Notificatie;
  onNavigate: (n: Notificatie) => void;
  onToggleGelezen: (n: Notificatie) => void;
}) {
  return (
    <div
      className={clsx(
        'flex items-start gap-3 px-4 py-3.5 border-b border-gray-100 transition-colors group cursor-pointer',
        !notificatie.gelezen ? 'bg-blue-50/40 hover:bg-blue-50/70' : 'hover:bg-gray-50'
      )}
      onClick={() => onNavigate(notificatie)}
    >
      {/* Icon */}
      <div className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
        <TypeIcoon type={notificatie.type} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <span
              className={clsx(
                'text-sm leading-tight block',
                !notificatie.gelezen ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
              )}
            >
              {notificatie.titel}
            </span>
            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notificatie.bericht}</p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {!notificatie.gelezen && (
              <span className="w-2 h-2 rounded-full bg-gold flex-shrink-0" />
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium">
            {typeLabelKort(notificatie.type)}
          </span>
          <span className="text-[10px] text-gray-400">{tijdGeleden(notificatie.createdAt)}</span>
        </div>
      </div>

      {/* Toggle read/unread */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleGelezen(notificatie);
        }}
        className="mt-1 p-1.5 rounded-md text-gray-400 hover:text-gold hover:bg-gold/10 transition-colors opacity-0 group-hover:opacity-100"
        title={notificatie.gelezen ? 'Markeer als ongelezen' : 'Markeer als gelezen'}
      >
        {notificatie.gelezen ? <Mail size={14} /> : <MailOpen size={14} />}
      </button>
    </div>
  );
}

// ─── Preferences types ──────────────────────────────────────────

interface NotifPrefs {
  newMessage: boolean;
  mention: boolean;
  milestoneChange: boolean;
  documentSigned: boolean;
  dossierUpdate: boolean;
  deliveryUpdate: boolean;
  systemAlert: boolean;
  emailEnabled: boolean;
}

const PREF_LABELS: { key: keyof NotifPrefs; label: string; description: string }[] = [
  { key: 'newMessage', label: 'Nieuwe berichten', description: 'Meldingen bij inkomende berichten' },
  { key: 'mention', label: 'Vermeldingen', description: 'Wanneer je vermeld wordt in een bericht' },
  { key: 'milestoneChange', label: 'Mijlpaal wijzigingen', description: 'Statuswijzigingen van dossiers' },
  { key: 'documentSigned', label: 'Document ondertekend', description: 'Wanneer een document ondertekend wordt' },
  { key: 'dossierUpdate', label: 'Dossier updates', description: 'Wijzigingen aan je dossiers' },
  { key: 'deliveryUpdate', label: 'Leveringsupdates', description: 'Goederen ontvangen, gescand of geleverd' },
  { key: 'systemAlert', label: 'Systeemmeldingen', description: 'Belangrijke systeemmeldingen' },
  { key: 'emailEnabled', label: 'Email notificaties', description: 'Ontvang meldingen ook per email' },
];

// ─── Pagina ─────────────────────────────────────────────────────

export function NotificatieCenterPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeFilter, setActiveFilter] = useState<FilterType>('ALLE');
  const [showPrefs, setShowPrefs] = useState(false);

  const typeParam = activeFilter === 'ALLE' ? undefined : activeFilter;
  const { data: notificaties, isLoading } = useNotificaties({ limit: 100, type: typeParam });
  const { data: countData } = useNotificatieCount();
  const markeerGelezen = useMarkeerGelezen();
  const markeerOngelezen = useMarkeerOngelezen();
  const markeerAlles = useMarkeerAllesGelezen();

  // Preferences
  const { data: prefs } = useQuery({
    queryKey: ['notificatie-prefs'],
    queryFn: () => apiFetch<NotifPrefs>('/api/communicatie/notifications/preferences'),
    enabled: showPrefs,
  });

  const updatePref = useMutation({
    mutationFn: (data: Partial<NotifPrefs>) =>
      apiFetch<NotifPrefs>('/api/communicatie/notifications/preferences', {
        method: 'PATCH',
        body: JSON.stringify(data),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificatie-prefs'] });
    },
  });

  const ongelezen = countData?.ongelezen ?? 0;

  // Real-time updates
  const handleNewNotificatie = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['notificaties'] });
  }, [queryClient]);
  useSocketEvent('notificatie:new', handleNewNotificatie);

  const handleNavigate = (n: Notificatie) => {
    if (!n.gelezen) {
      markeerGelezen.mutate(n.id);
    }
    if (n.actionUrl) {
      navigate(n.actionUrl);
    } else if (n.dossierId) {
      navigate(`/dossiers/${n.dossierId}`);
    }
  };

  const handleToggleGelezen = (n: Notificatie) => {
    if (n.gelezen) {
      markeerOngelezen.mutate(n.id);
    } else {
      markeerGelezen.mutate(n.id);
    }
  };

  // Groepeer notificaties per datum
  const gegroepeerd = (notificaties ?? []).reduce<Record<string, Notificatie[]>>((acc, n) => {
    const groep = datumGroep(n.createdAt);
    if (!acc[groep]) acc[groep] = [];
    acc[groep].push(n);
    return acc;
  }, {});

  const groepVolgorde = ['Vandaag', 'Gisteren', 'Deze week', 'Deze maand', 'Ouder'];

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-900">Notificaties</h1>
          {ongelezen > 0 && (
            <span className="bg-gold/10 text-gold text-xs font-semibold px-2 py-0.5 rounded-full">
              {ongelezen} ongelezen
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {ongelezen > 0 && (
            <button
              onClick={() => markeerAlles.mutate()}
              className="flex items-center gap-1.5 text-sm text-gold hover:text-gold/80 transition-colors font-medium"
            >
              <CheckCheck size={16} />
              Alles gelezen
            </button>
          )}
          <button
            onClick={() => setShowPrefs((p) => !p)}
            className={clsx(
              'flex items-center gap-1.5 text-sm transition-colors font-medium',
              showPrefs ? 'text-gold' : 'text-gray-400 hover:text-gray-600'
            )}
          >
            <Settings size={16} />
            Voorkeuren
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1">
        <Filter size={14} className="text-gray-400 flex-shrink-0 mr-1" />
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setActiveFilter(f.key)}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap',
              activeFilter === f.key
                ? 'bg-gold text-white'
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            )}
          >
            {f.icon}
            {f.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !notificaties || notificaties.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Bell size={40} className="mb-3 text-gray-300" />
            <p className="text-sm font-medium">Geen meldingen</p>
            <p className="text-xs mt-1">
              {activeFilter !== 'ALLE'
                ? 'Geen meldingen voor dit filter'
                : 'Je bent helemaal bij!'}
            </p>
          </div>
        ) : (
          groepVolgorde
            .filter((g) => gegroepeerd[g]?.length)
            .map((groep) => (
              <div key={groep}>
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                    {groep}
                  </span>
                </div>
                {gegroepeerd[groep].map((n) => (
                  <NotificatieRij
                    key={n.id}
                    notificatie={n}
                    onNavigate={handleNavigate}
                    onToggleGelezen={handleToggleGelezen}
                  />
                ))}
              </div>
            ))
        )}
      </div>

      {/* Preferences panel */}
      {showPrefs && prefs && (
        <div className="mt-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-900">Notificatie voorkeuren</h2>
            <p className="text-xs text-gray-500 mt-0.5">Kies welke meldingen je wilt ontvangen</p>
          </div>
          <div className="divide-y divide-gray-100">
            {PREF_LABELS.map(({ key, label, description }) => (
              <label
                key={key}
                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <div>
                  <span className="text-sm font-medium text-gray-800">{label}</span>
                  <p className="text-xs text-gray-500">{description}</p>
                </div>
                <input
                  type="checkbox"
                  checked={prefs[key] ?? true}
                  onChange={(e) => updatePref.mutate({ [key]: e.target.checked })}
                  className="w-4 h-4 text-gold accent-brand rounded focus:ring-gold"
                />
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
